<template>
  <div :class="'form-dp form-dp--' + variant">
    <global-events
      target="window"
      @resize="onWindowResize"
      @click="onOutsideClick"
      @keydown.enter.capture="onKeydownEnter"
      @keydown.esc.exact="close"
    />
    <div
      ref="wrapper"
      class="form-dp__wrapper"
      @click="onInsideClick"
    >
      <form-input
        :id="id"
        ref="input"
        :value="dateApplied"
        :placeholder="isIndefinite ? $t('terms.indefinite') : placeholder"
        :label="label"
        :validation="isCreateDatePicker ? { required: required, date_format: 'dd.MM.yyyy', after: lastDate} : { required: required, date_format: 'dd.MM.yyyy'}"
        :data-testing="dataTesting"
        type="date"
        @oninput="onInput"
      >
        <template slot="additional">
          <icon
            icon="date"
            color="red"
            color-hover="red"
            size="19"
            emits-click-event
            class="form-dp__reset"
            @click="open"
          />
        </template>
      </form-input>

      <transition name="fade">
        <div
          v-show="isOpen"
          ref="dropdown"
          :class="'form-dp__dropdown form-dp__dropdown--' + positionDropdown + ' form-dp__dropdown--' + variant"
          @click="onDropdownClick"
        >
          <div
            class="form-dp__dropdown-inner"
            @click="onDropdownInnerClick"
          >
            <div class="form-dp__close-wrapper">
              <icon
                icon="close"
                color="red"
                color-hover="grey"
                size="12"
                emits-click-event
                class="form-dp__close"
                @click="close()"
              />
            </div>
            <b-calendar
              v-model="calendarDate"
              :min="calendarMinDate"
              :locale="calendarLocale"
              :class="{
                'form-dp__picker': true,
                'form-dp__picker--disabled': isIndefinite
              }"
              hide-header
              label-help=""
              start-weekday="1"
              block
              @selected="onCalendarSelect"
            />
            <form-checkbox
              v-if="variant === 'indefinite'"
              :id="`${id}_checkbox`"
              :value="isIndefinite"
              class="form-dp__checkbox"
              @onchange="onChangeIsIndefinite"
            >
              <span v-text="$t('terms.indefinite')" />
            </form-checkbox>
            <btn
              :disabled="isApplyDisabled"
              variant="primary"
              class="form-dp__btn"
              @click="apply"
              v-html="$t('button.apply')"
            />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { BFormGroup, BCalendar } from 'bootstrap-vue';
import GlobalEvents from 'vue-global-events';
import Btn from '@components/Btn/Btn';
import { dateCreateMoment, dateFn, dateToday, dateAddOneYear, dateFromString, dateYesterday } from '@theme/utils/datefns';
import FormCheckbox from '@components/FormCheckbox/FormCheckbox';
import Icon from '@atoms/Icon/Icon';
import FormInput from '@components/FormInput/FormInput';
/**
 * Form datepicker field
 */
export default {
  name: 'FormDatePicker',
  components: {
    BFormGroup,
    GlobalEvents,
    BCalendar,
    Btn,
    FormCheckbox,
    Icon,
    FormInput
  },
  inject: {
    $validator: '$validator'
  },
  props: {
    /**
     * Id of form component
     */
    id: {
      type: String,
      required: true
    },
    /**
     * Sets the variant of the component
     */
    variant: {
      type: String,
      default: 'default',
      validator: value => ['default', 'filter', 'indefinite', 'year'].includes(value)
    },
    /**
     * Date value that is set initially
     */
    value: {
      type: Date,
      default: null
    },
    /**
     * Minimum date to be valid
     */
    minDate: {
      type: [Date, String],
      default: null
    },
    /**
     * Label for the form group
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Placeholder set initially
     */
    placeholder: {
      type: String,
      default: ''
    },
    /**
     * Required attribute
     */
    required: {
      type: Boolean,
      default: false
    },
    /**
     * Variable for cypress testing
     */
    dataTesting: {
      type: String,
      default: null
    },
    /**
     * Checks if the date is in the past
     */
    isCreateDatePicker: {
      type: Boolean,
      default: false,
    },
    /**
     * References valid_from
     */
    validFrom: {
      type: [Date, String],
      default: ''
    }
  },
  data() {
    return {
      isOpen: false,
      date: null,
      dateOld: null,
      dateApplied: null,
      isIndefinite: false,
      internalMinDate: this.minDate ? dateCreateMoment(this.minDate) : dateToday(),
      positionDropdown: '',
      hasFocus: false
    };
  },

  computed: {
    isApplyDisabled() {
      return !this.isIndefinite && (!this.date || this.date === this.dateOld);
    },
    lastDate() {
      return this.validFrom !== '' ? dateFn(this.validFrom) : dateYesterday();
    },
    // Convert internal date (DD.MM.YYYY string) to YYYY-MM-DD for b-calendar
    calendarDate: {
      get() {
        if (!this.date) return null;
        return dateCreateMoment(this.date, 'DD.MM.YYYY').format('YYYY-MM-DD');
      },
      set(val) {
        if (val) {
          this.date = dateCreateMoment(val, 'YYYY-MM-DD').format('DD.MM.YYYY');
        } else {
          this.date = null;
        }
      }
    },
    // Convert minDate to YYYY-MM-DD for b-calendar
    calendarMinDate() {
      if (this.variant === 'filter') return null;
      if (!this.internalMinDate) return null;
      return dateCreateMoment(this.internalMinDate).format('YYYY-MM-DD');
    },
    calendarLocale() {
      return 'de-AT';
    }
  },
  watch: {
    minDate(value) {
      if (value) {
        this.internalMinDate = dateCreateMoment(value);

        if (this.dateApplied) {
          if (dateFromString(this.dateApplied).isBefore(dateCreateMoment(value))) {
            this.reset();
          }
        }
      } else {
        this.internalMinDate = null;
      }
    }
  },
  mounted() {
    switch (this.variant) {
      case 'filter':
        this.internalMinDate = null;
        break;
      case 'year':
        if (!this.value) {
          this.date = dateFn(dateAddOneYear(this.internalMinDate));
          this.dateApplied = this.date;
        }
    }

    if (this.value) {
      if (dateCreateMoment(this.value).isAfter(this.internalMinDate)) {
        this.date = dateFn(this.value);
        this.dateApplied = this.date;
      } else {
        this.date = dateFn(this.internalMinDate);
        this.dateApplied = this.date;
      }
    }
  },
  methods: {
    /**
     * Re-calculates the positioning on resizing the window
     * @event onWindowResize
     * @type {Resize}
     */
    onWindowResize() {
      this.calcPosition();
    },

    /**
     * Stops the event propagation if the user clicks inside the dropdown div
     * @event onInsideClick
     * @type {Click}
     */
    onInsideClick(event) {
      event.stopPropagation();
    },

    /**
     * Closes the dropdown if the user clicks outside the component
     * @event onOutsideClick
     * @type {Click}
     */
    onOutsideClick(event) {
      this.apply();
    },

    /**
     * Has the same effect as onOutsideClick() if the dropdown is a modal
     * @event onDropdownClick
     * @type {Click}
     */
    onDropdownClick() {
      if (this.positionDropdown === 'modal') {
        this.apply();
      }
    },

    /**
     * Stops event propagation if the dropdown is a modal
     * @event onDropdownClick
     * @type {Click}
     */
    onDropdownInnerClick(event) {
      if (this.positionDropdown === 'modal') {
        event.stopPropagation();
      }
    },

    /**
     * Changes the isIndefinite variable
     * @event onChangeIsIndefinite
     * @type {Change}
     */
    onChangeIsIndefinite(param) {
      this.isIndefinite = param.val;

      if (this.isIndefinite) this.date = null;
    },

    /**
     * Calls apply if isOpen or opens the dropdown if not.
     * @event onKeydownEnter
     * @type {Keydown}
     */
    onKeydownEnter(event) {
      if (this.isOpen) {
        event.stopPropagation();
        event.preventDefault();
        this.apply();
      } else if (this.hasFocus) {
        event.stopPropagation();
        event.preventDefault();
        this.open();
      }
    },

    /**
     * Opens the datepicker dropdown
     */
    open() {
      this.calcPosition();
      this.isOpen = true;
      this.date = this.dateApplied;
      this.dateOld = this.dateApplied;
      this.$emit('open');
    },

    /**
     * Calculates the positioning of the dropdown
     * @method calcPosition
     */
    calcPosition() {
      const inputRect = this.$refs.input.$refs.input.getBoundingClientRect();
      const wrapperWidth = this.$refs.wrapper.offsetWidth;
      const datepickerHeight = Math.max(wrapperWidth - 40, 270) * (6/7);
      const dropdownHeight = this.variant === 'indefinite' ? 270 + datepickerHeight : 217 + datepickerHeight;

      if (wrapperWidth < 310) {
        const offsetLeft = (wrapperWidth - 310) / 2;
        this.$refs.dropdown.style.left = offsetLeft + 'px';
      } else {
        this.$refs.dropdown.style.left = 0;
      }

      if (window.innerHeight - inputRect.bottom >= dropdownHeight) {
        this.positionDropdown = '';
      } else if (window.innerHeight - inputRect.bottom < dropdownHeight && inputRect.top > dropdownHeight) {
        this.positionDropdown = 'above';
      } else {
        this.positionDropdown = 'modal';
        this.$refs.dropdown.style.left = 0;
      }
    },

    clear() {
      if (this.variant === 'default') {
        this.date = dateFn(this.internalMinDate);
        this.dateApplied = this.date;
      } else if (this.variant === 'indefinite') {
        this.date = null;
        this.dateApplied = null;
      } else if (this.variant === 'year') {
        this.date = dateFn(dateAddOneYear(this.internalMinDate));
        this.dateApplied = this.date;
      } else {
        this.date = null;
        this.dateApplied = null;
      }
    },

    /**
     * Resets the component and sets the date to null or the min-date, if the variant is default
     * @method reset
     */
    reset() {
      this.clear();

      this.$emit('change', this.dateApplied);
    },

    /**
     * Applies the selected date to the input field
     * @method apply
     */
    apply() {
      if (this.isOpen) {
        this.isOpen = false;
        this.dateApplied = this.isIndefinite ? null : this.date;
        this.$emit('change', this.dateApplied);
      }
    },

    /**
     * Closes the dropdown
     */
    close() {
      this.isOpen = false;
    },

    /**
     * Handle date input event
     * @event onInput
     * @type {oninput}
     */
    onInput(value) {
      this.dateApplied = value;
      this.date = value;

      if (this.variant === 'indefinite') {
        this.isIndefinite = value === '';
      }

      this.$emit('change', value);
    },

    /**
     * Handle calendar date selection
     * @event onCalendarSelect
     * @type {selected}
     */
    onCalendarSelect(date) {
      // Date is already set via v-model, this is for any additional handling
    }
  },
};

</script>

<style lang="scss">
  @import './FormDatePicker';
</style>
